// src/features/feeds/components/FeedGridRow.tsx

import { memo, type CSSProperties, type MouseEvent } from 'react';
import { IconCode, IconDots } from '@tabler/icons-react';
import type { FeedGridRow as FeedGridRowData } from '@/lib/types/Feed';
import styles from './FeedGrid.module.css';

interface FeedGridRowProps {
  feed: FeedGridRowData;
  index: number;
  isSelected: boolean;
  onSelectOnly: (id: number, index: number) => void;
  onToggle: (id: number, index: number) => void;
  onRangeSelect: (id: number, index: number) => void;
  onMenuOpen: (index: number, buttonEl: HTMLElement) => void;
  onNavigate: (href: string) => void;
  onPublish: (feed: FeedGridRowData) => void;
  style: CSSProperties;
}

/**
 * Single flat row component -- all content inlined as native HTML elements.
 * Zero Mantine/Router component instances per row for minimal reconciliation cost.
 */
function FeedGridRowComponent({
  feed,
  index,
  isSelected,
  onSelectOnly,
  onToggle,
  onRangeSelect,
  onMenuOpen,
  onNavigate,
  onPublish,
  style,
}: FeedGridRowProps) {
  const handleClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;

    if (target.closest('[data-cell="checkbox"]')) {
      onToggle(feed.feedId, index);
      return;
    }

    if (target.closest('[data-cell="actions"]')) {
      return;
    }

    if (target.closest('[data-cell="title"] a')) {
      return;
    }

    if (e.shiftKey) {
      e.preventDefault();
      onRangeSelect(feed.feedId, index);
    } else if (e.ctrlKey || e.metaKey) {
      onToggle(feed.feedId, index);
    } else {
      onSelectOnly(feed.feedId, index);
    }
  };

  const handleTitleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      onNavigate(feed.href);
    }
  };

  return (
    <div
      className={`${styles.row} ${styles.gridColumns} ${isSelected ? styles.rowSelected : ''}`}
      style={style}
      onClick={handleClick}
      data-selected={isSelected}
    >
      {/* Checkbox */}
      <div className={`${styles.cell} ${styles.cellCheckbox}`} data-cell="checkbox">
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            className={styles.checkboxInput}
            checked={isSelected}
            readOnly
            tabIndex={-1}
            aria-label={`Select ${feed.title}`}
            onClick={(e) => e.stopPropagation()}
          />
          <span className={styles.checkboxControl} />
        </label>
      </div>

      {/* Title + display ID + source count */}
      <div className={`${styles.cell} ${styles.cellTitle}`} data-cell="title">
        <div className={styles.titleContainer}>
          <a href={feed.href} className={styles.titleLink} onClick={handleTitleClick}>
            {feed.title}
          </a>
          <div className={styles.titleSubline}>
            <span className={styles.titleId}>{feed.displayId}</span>
            <span className={styles.titleDot}>&middot;</span>
            <span className={styles.sourceCount}>
              {feed.sourceCount} {feed.sourceCount === 1 ? 'source' : 'sources'}
            </span>
          </div>
        </div>
      </div>

      {/* Status */}
      <div className={`${styles.cell} ${styles.cellStatus}`}>
        <span className={styles.statusBadge} data-color={feed.isActive ? 'green' : 'gray'}>
          {feed.isActive ? 'Active' : 'Inactive'}
        </span>
      </div>

      {/* Created */}
      <div className={`${styles.cell} ${styles.cellDate}`}>
        {feed.createdFormatted ? (
          <span title={feed.createdTooltip ?? undefined}>{feed.createdFormatted}</span>
        ) : '-'}
      </div>

      {/* Updated */}
      <div className={`${styles.cell} ${styles.cellDate}`}>
        <span title={feed.updatedTooltip}>{feed.updatedFormatted}</span>
      </div>

      {/* Actions: Publish + Kebab */}
      <div className={`${styles.cell} ${styles.cellAction}`} data-cell="actions">
        <button
          className={styles.publishButton}
          onClick={(e) => { e.stopPropagation(); onPublish(feed); }}
          disabled={!feed.isActive}
          aria-label="Publish"
          type="button"
          title={feed.isActive ? 'Publish' : 'Activate feed to publish'}
        >
          <IconCode size={16} />
        </button>
        <button
          className={styles.kebabButton}
          onClick={(e) => { e.stopPropagation(); onMenuOpen(index, e.currentTarget); }}
          aria-label="Actions"
          type="button"
        >
          <IconDots size={16} />
        </button>
      </div>
    </div>
  );
}

export const FeedGridRow = memo(FeedGridRowComponent, (prev, next) => {
  return (
    prev.feed === next.feed &&
    prev.index === next.index &&
    prev.isSelected === next.isSelected &&
    prev.style.transform === next.style.transform &&
    prev.style.height === next.style.height
  );
});
